import BaseComponent from 'bootstrap/js/src/base-component.js';
import EventHandler from 'bootstrap/js/src/dom/event-handler.js';
import SelectorEngine from 'bootstrap/js/src/dom/selector-engine.js';
import { defineJQueryPlugin } from 'bootstrap/js/src/util/index.js';

const NAME = 'videoModal';
const EVENT_SHOW = 'show.bs.modal';
const EVENT_HIDDEN = 'hidden.bs.modal';
const SELECTOR_MODAL = '.modal';
const SELECTOR_DIALOG = '.modal-dialog-media';
const SELECTOR_RATIO = '.ratio';

let youTubeAPIInitPromise = null;
let vimeoAPIInitPromise = null;

/**
 * Load the YouTube iframe API only when a YouTube video is opened.
 */
function loadYouTubeAPI() {
  if (globalThis.YT?.Player) {
    return Promise.resolve();
  }

  if (youTubeAPIInitPromise) {
    return youTubeAPIInitPromise;
  }

  youTubeAPIInitPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      'script[src="https://www.youtube.com/iframe_api"]'
    );

    const previousReady = globalThis.onYouTubeIframeAPIReady;

    globalThis.onYouTubeIframeAPIReady = () => {
      try {
        if (typeof previousReady === 'function') {
          previousReady();
        }
      } catch {}

      resolve();
    };

    if (existingScript) {
      if (globalThis.YT?.Player) {
        resolve();
      }

      return;
    }

    const script = document.createElement('script');

    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;

    script.addEventListener('error', () => {
      youTubeAPIInitPromise = null;
      reject(new Error('Failed to load YouTube iframe API'));
    });

    document.head.append(script);
  });

  return youTubeAPIInitPromise;
}

/**
 * Load the Vimeo Player API only when a Vimeo video is opened.
 */
function loadVimeoAPI() {
  if (globalThis.Vimeo?.Player) {
    return Promise.resolve();
  }

  if (vimeoAPIInitPromise) {
    return vimeoAPIInitPromise;
  }

  vimeoAPIInitPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      'script[src="https://player.vimeo.com/api/player.js"]'
    );

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });

      existingScript.addEventListener(
        'error',
        () => {
          vimeoAPIInitPromise = null;
          reject(new Error('Failed to load Vimeo Player API'));
        },
        { once: true }
      );

      return;
    }

    const script = document.createElement('script');

    script.src = 'https://player.vimeo.com/api/player.js';
    script.async = true;

    script.addEventListener('load', () => resolve(), { once: true });

    script.addEventListener(
      'error',
      () => {
        vimeoAPIInitPromise = null;
        reject(new Error('Failed to load Vimeo Player API'));
      },
      { once: true }
    );

    document.head.append(script);
  });

  return vimeoAPIInitPromise;
}

class VideoModal extends BaseComponent {
  constructor(element) {
    super(element);

    this._dialog = SelectorEngine.findOne(SELECTOR_DIALOG, this._element);

    this._container = SelectorEngine.findOne(SELECTOR_RATIO, this._element);

    this._youtubeSrc = this._container?.getAttribute('data-src');

    this._vimeoUrl = this._container?.getAttribute('data-vimeo-url');

    this._youtubePlayer = null;
    this._vimeoPlayer = null;
  }

  dispose() {
    this._onHide();
    super.dispose();
  }

  async _onShow() {
    const { id } = this._element;
    const playerID = `video-${id}-youtube-player`;

    try {
      if (this._vimeoUrl) {
        await loadVimeoAPI();

        const vimeoId = this._getVimeoId(this._vimeoUrl);

        if (!vimeoId) {
          return;
        }

        this._vimeoPlayer = new globalThis.Vimeo.Player(this._container, {
          id: vimeoId,
          autoplay: true
        });

        this._element.dataset.vimeoId = String(vimeoId);

        this._element.__unlkVimeoPlayer = this._vimeoPlayer;

        this._element.dispatchEvent(
          new CustomEvent('videomodal.ready', {
            bubbles: true,
            detail: {
              provider: 'vimeo',
              player: this._vimeoPlayer,
              videoId: vimeoId
            }
          })
        );

        return;
      }

      if (this._youtubeSrc) {
        await loadYouTubeAPI();

        const youtubeId = this._getYouTubeId(this._youtubeSrc);

        if (!youtubeId) {
          return;
        }

        const playerDiv = this._container.querySelector(`#${playerID}`);

        if (!playerDiv) {
          return;
        }

        this._youtubePlayer = new globalThis.YT.Player(playerDiv, {
          videoId: youtubeId,
          playerVars: {
            autoplay: 1,
            rel: 0
          }
        });

        this._element.dataset.ytId = youtubeId;

        this._element.__unlkYtPlayer = this._youtubePlayer;

        this._element.dispatchEvent(
          new CustomEvent('videomodal.ready', {
            bubbles: true,
            detail: {
              provider: 'youtube',
              player: this._youtubePlayer,
              videoId: youtubeId
            }
          })
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[VideoModal] Failed to initialize video player:', error);
    }
  }

  _onHide() {
    if (this._vimeoPlayer) {
      this._vimeoPlayer.unload().catch(() => {});

      this._vimeoPlayer = null;
    }

    if (this._youtubePlayer) {
      this._youtubePlayer.destroy();
      this._youtubePlayer = null;
    }

    delete this._element.__unlkYtPlayer;
    delete this._element.dataset.ytId;

    delete this._element.__unlkVimeoPlayer;
    delete this._element.dataset.vimeoId;
  }

  _getYouTubeId(url) {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([\w-]{11})/);

    return match ? match[1] : '';
  }

  _getVimeoId(url) {
    const match = url.match(/vimeo\.com\/(\d+)/);

    return match ? match[1] : '';
  }

  static get NAME() {
    return NAME;
  }

  static jQueryInterface(config) {
    return this.each(function () {
      VideoModal.getOrCreateInstance(this, config);
    });
  }
}

EventHandler.on(document, EVENT_SHOW, SELECTOR_MODAL, function () {
  if (!SelectorEngine.findOne(SELECTOR_DIALOG, this)) {
    return;
  }

  VideoModal.getOrCreateInstance(this)._onShow();
});

EventHandler.on(document, EVENT_HIDDEN, SELECTOR_MODAL, function () {
  if (!SelectorEngine.findOne(SELECTOR_DIALOG, this)) {
    return;
  }

  VideoModal.getOrCreateInstance(this)._onHide();
});

defineJQueryPlugin(VideoModal);

export default VideoModal;
